import { parse, stringify } from 'yaml'
import type { Rule, Action } from '@/api/types'

// ── Action catalogue (mirrors internal/policy actions) ───────────────────────────
// `param` drives the editor's param input; `key` is the params map key read/written.
export type ParamKind = 'band' | 'number' | 'date' | 'text' | 'textarea' | 'note'
export interface ActionDef {
  value: string
  label: string
  param: ParamKind
  key: string
  group: string
}

export const BANDS = ['critical', 'high', 'medium', 'low', 'info']

export const ACTIONS: ActionDef[] = [
  { value: 'force_band', label: 'Force band', param: 'band', key: 'band', group: 'Scoring' },
  { value: 'min_band', label: 'Minimum band', param: 'band', key: 'band', group: 'Scoring' },
  { value: 'set_score', label: 'Set score', param: 'number', key: 'score', group: 'Scoring' },
  { value: 'clamp', label: 'Clamp score (max)', param: 'number', key: 'max', group: 'Scoring' },
  { value: 'adjust_weight', label: 'Adjust weight (delta)', param: 'number', key: 'delta', group: 'Scoring' },
  { value: 'set_due_date', label: 'Set due date', param: 'date', key: 'date', group: 'Scoring' },
  { value: 'set_sla', label: 'Set SLA date', param: 'date', key: 'date', group: 'Scoring' },
  { value: 'tag', label: 'Tag', param: 'text', key: 'tag', group: 'Scoring' },
  { value: 'add_guidance', label: 'Add guidance', param: 'textarea', key: 'guidance', group: 'Scoring' },
  { value: 'require_approval', label: 'Require approval', param: 'note', key: 'note', group: 'Evidence-only' },
  { value: 'propose_waiver', label: 'Propose waiver', param: 'note', key: 'note', group: 'Evidence-only' },
  { value: 'escalate', label: 'Escalate', param: 'note', key: 'note', group: 'Evidence-only' },
  { value: 'exempt', label: 'Exempt', param: 'note', key: 'note', group: 'Evidence-only' },
  { value: 'map_framework', label: 'Map framework', param: 'text', key: 'framework', group: 'Evidence-only' },
]

export const actionDef = (a: string): ActionDef => ACTIONS.find((d) => d.value === a) ?? ACTIONS[0]

// describeAction returns a human-readable label + detail for a rule action, plus its
// group ('Scoring' | 'Evidence-only' | 'unknown') and param kind so previews can style
// it (e.g. render band-valued details as a coloured band badge). Unknown actions fall
// back to the raw action name + JSON params.
export function describeAction(a: Action): {
  label: string
  detail: string
  group: string
  param: ParamKind | 'unknown'
} {
  const def = ACTIONS.find((d) => d.value === a.action)
  if (!def) {
    const params = a.params ?? {}
    return {
      label: a.action,
      detail: Object.keys(params).length ? JSON.stringify(params) : '',
      group: 'unknown',
      param: 'unknown',
    }
  }
  const v = a.params?.[def.key]
  return {
    label: def.label,
    detail: v == null || v === '' ? '' : String(v),
    group: def.group,
    param: def.param,
  }
}

// OP_LABELS humanizes CEL comparison operators for non-technical readers. The raw
// operator stays available for tooltips where exactness matters.
export const OP_LABELS: Record<string, string> = {
  '==': 'is',
  '!=': 'is not',
  '>=': 'at least',
  '<=': 'at most',
  '>': 'over',
  '<': 'under',
}

// fieldLabel returns a friendly name for a CEL field path (e.g. `ctx.hndl_window_years`
// → `HNDL window (yrs)`), falling back to the prettified last dotted segment for unknown
// paths. Full path kept for a tooltip by callers.
export function fieldLabel(path: string): string {
  const def = CEL_FIELDS.find((f) => f.path === path)
  if (def) return def.label
  const seg = path.slice(path.lastIndexOf('.') + 1)
  return seg.replace(/_/g, ' ')
}

// ruleSentence synthesises a plain-English one-liner for a rule so a reader grasps intent
// without decoding chips (e.g. "When algorithm is RSA and quantum risk is vulnerable, force
// band critical."). Advanced CEL is quoted verbatim; a condition-less rule reads "For every
// asset, …".
export function ruleSentence(rule: Rule): string {
  const preds = celToPredicates(rule.when_expr)
  let cond: string
  if (preds === null) {
    cond = `When ${rule.when_expr}`
  } else if (preds.length === 0) {
    cond = 'For every asset'
  } else {
    cond =
      'When ' +
      preds.map((p) => `${fieldLabel(p.field)} ${OP_LABELS[p.op] ?? p.op} ${p.value}`).join(' and ')
  }

  const acts = (rule.then_actions ?? []).map((a) => {
    const { label, detail } = describeAction(a)
    return detail ? `${label.toLowerCase()} ${detail}` : label.toLowerCase()
  })
  const then = acts.length ? acts.join(', ') : 'no action'

  const s = `${cond}, ${then}.`
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── Known CEL fields (mirror internal/policy/cel.go context vars) ──────────────
export type FieldKind = 'text' | 'number' | 'enum'
export interface FieldDef {
  path: string
  kind: FieldKind
  options?: string[]
  label: string // friendly name for previews / sentences
}

export const CEL_FIELDS: FieldDef[] = [
  { path: 'asset.algorithm', kind: 'text', label: 'algorithm' },
  { path: 'asset.key_size', kind: 'number', label: 'key size' },
  { path: 'asset.asset_type', kind: 'text', label: 'asset type' },
  {
    path: 'asset.quantum_risk',
    kind: 'enum',
    options: ['vulnerable', 'partial', 'hybrid', 'safe', 'unknown'],
    label: 'quantum risk',
  },
  { path: 'asset.material_state', kind: 'text', label: 'material state' },
  {
    path: 'inventory.criticality',
    kind: 'enum',
    options: ['low', 'medium', 'high', 'critical'],
    label: 'inventory criticality',
  },
  {
    path: 'inventory.data_classification',
    kind: 'enum',
    options: ['public', 'internal', 'confidential', 'restricted', 'regulated'],
    label: 'data classification',
  },
  { path: 'inventory.regulatory_scope', kind: 'text', label: 'regulatory scope' },
  {
    path: 'ctx.band',
    kind: 'enum',
    options: ['critical', 'high', 'medium', 'low', 'info'],
    label: 'risk band',
  },
  { path: 'ctx.exposure', kind: 'number', label: 'exposure' },
  { path: 'ctx.hndl_window_years', kind: 'number', label: 'HNDL window (yrs)' },
]

export const CEL_OPS = ['==', '!=', '<', '<=', '>', '>='] as const

export interface Predicate {
  field: string
  op: string
  value: string
}

function fieldKind(path: string): FieldKind {
  return CEL_FIELDS.find((f) => f.path === path)?.kind ?? 'text'
}

function isNumericLiteral(v: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(v.trim())
}

// predicatesToCel joins simple field/op/value rows with AND, quoting non-numeric values.
export function predicatesToCel(rows: Predicate[]): string {
  return rows
    .filter((r) => r.field && r.op)
    .map((r) => {
      const numeric = fieldKind(r.field) === 'number' || (fieldKind(r.field) === 'text' && isNumericLiteral(r.value))
      const val = numeric ? r.value.trim() : `"${r.value.replace(/"/g, '\\"')}"`
      return `${r.field} ${r.op} ${val}`
    })
    .join(' && ')
}

// celToPredicates parses a simple AND-of-comparisons CEL expression back into rows.
// Returns null when the expression uses OR, parentheses, or function calls (→ advanced/raw).
export function celToPredicates(expr: string): Predicate[] | null {
  const e = expr.trim()
  if (e === '') return []
  if (/\|\||[()]/.test(e)) return null
  if (/[A-Za-z_][\w.]*\s*\(/.test(e)) return null // function call, e.g. daysUntil(...)

  const parts = e.split('&&')
  const rows: Predicate[] = []
  for (const raw of parts) {
    const m = raw.trim().match(/^([A-Za-z_][\w.]*)\s*(==|!=|<=|>=|<|>)\s*(.+?)$/)
    if (!m) return null
    let value = m[3].trim()
    const quoted = /^"(.*)"$/.exec(value)
    if (quoted) value = quoted[1].replace(/\\"/g, '"')
    rows.push({ field: m[1], op: m[2], value })
  }
  return rows
}

// ── YAML <-> Rule (single-rule doc; keys mirror internal/policy/yaml.go) ───────
interface YamlRule {
  id?: string
  priority?: number
  framework_ref?: string[]
  when?: string
  then?: Action[]
  effective_from?: string
  effective_to?: string
}

export function ruleToYaml(rule: Rule): string {
  const y: YamlRule = {
    id: rule.rule_id,
    priority: rule.priority,
    when: rule.when_expr,
    then: rule.then_actions ?? [],
  }
  if (rule.framework_ref?.length) y.framework_ref = rule.framework_ref
  if (rule.effective_from) y.effective_from = rule.effective_from.slice(0, 10)
  if (rule.effective_to) y.effective_to = rule.effective_to.slice(0, 10)
  return stringify(y)
}

// yamlToRule parses a single-rule YAML document into a Rule. Throws on parse/shape error.
export function yamlToRule(text: string): Rule {
  const y = parse(text) as YamlRule | null
  if (!y || typeof y !== 'object') throw new Error('YAML must be a single rule object')
  if (y.then != null && !Array.isArray(y.then)) throw new Error('`then` must be a list of actions')
  return {
    rule_id: y.id ?? '',
    priority: typeof y.priority === 'number' ? y.priority : 100,
    framework_ref: y.framework_ref ?? [],
    when_expr: y.when ?? '',
    then_actions: (y.then ?? []).map((a) => ({ action: a.action, params: a.params ?? {} })),
    effective_from: y.effective_from ?? null,
    effective_to: y.effective_to ?? null,
  }
}
